import { useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useParams } from 'react-router-dom';
import Toast from './components/Toast';
import Privacy from './components/Privacy';
import Terms from './components/Terms';
import About from './components/About';
import Contact from './components/Contact';
import Languages, { LANGUAGES } from './components/Languages';
import LanguagePage from './components/LanguagePage';
import Daily from './components/Daily';
import Group from './components/Group';
import Account from './components/Account';
import AccountButton from './components/AccountButton';
import AuthPanel from './components/AuthPanel';
import AuthVerify from './components/AuthVerify';
import AuthReset from './components/AuthReset';
import AuthUnsubscribeDaily from './components/AuthUnsubscribeDaily';
import AuthCallback from './components/AuthCallback';
import TermsGate from './components/TermsGate';
import ServerDiscovery from './services/ServerDiscovery';
import { SeoHelmet } from './hooks/useSeo';
import { SEO } from './seo/routes';
import { Analytics } from '@vercel/analytics/react';
import { trackEvent } from './utils/analytics';
import { getToken, isSignedIn, subscribeAuth } from './utils/auth';
import PolyvoxPromo from './components/PolyvoxPromo';
import PolyvoxPopup from './components/PolyvoxPopup';
import LangleAfterDetect from './components/LangleAfterDetect';
import UploadDetect from './components/UploadDetect';
import QuestionPage from './components/QuestionPage';
import { QUESTIONS } from './seo/questions';
import { polyvoxUrl, trackPolyvoxClick } from './utils/polyvox';

// Bare /langle: classic (the original mixed-pool daily) through 2026-09-09,
// Easy by default from 2026-09-10 on — must match backend's DIFFICULTY_START
// (backend/utils/daily.py). Classic keeps its own history/streaks and stays
// reachable at /langle/classic either way.
const EASY_DEFAULT_FROM = '2026-09-10';
function DailyLanding() {
  const todayUtc = new Date().toISOString().slice(0, 10);
  return <Daily difficulty={todayUtc >= EASY_DEFAULT_FROM ? 'easy' : undefined} />;
}

const MicIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M12 2a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
    <path d="M19 10v1a7 7 0 0 1-14 0v-1" />
    <line x1="12" y1="19" x2="12" y2="22" />
  </svg>
);

const StopIcon = () => (
  <svg viewBox="0 0 24 24">
    <rect x="6" y="6" width="12" height="12" rx="2" fill="currentColor" stroke="none" />
  </svg>
);

const GlobeIcon = () => (
  <svg className="result-icon" viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="10" />
    <path d="M2 12h20" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10Z" />
  </svg>
);

const DocIcon = () => (
  <svg className="result-icon" viewBox="0 0 24 24">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
    <path d="M14 2v6h6" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
  </svg>
);


const Waveform = () => (
  <div className="waveform">
    {Array.from({ length: 7 }, (_, i) => (
      <span key={i} className="waveform-bar" />
    ))}
  </div>
);

const Logo = () => (
  <svg className="logo-icon" viewBox="0 0 40 40" fill="none">
    <circle cx="20" cy="20" r="19" stroke="#0f0f0f" strokeWidth="2" fill="none" />
    <ellipse cx="20" cy="20" rx="8" ry="19" stroke="#0f0f0f" strokeWidth="1.5" fill="none" />
    <line x1="2" y1="20" x2="38" y2="20" stroke="#0f0f0f" strokeWidth="1.5" />
    <path d="M27 12a10 10 0 0 1 0 4" stroke="#0f0f0f" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M30 10a14 14 0 0 1 0 8" stroke="#0f0f0f" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M33 8a18 18 0 0 1 0 12" stroke="#0f0f0f" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

/** /languages/<slug>: head tags from the generated route, or back to the hub. */
function LanguageRoute() {
  const { slug } = useParams();
  const seo = SEO[`lang-${slug}`];
  if (!seo) return <Navigate to="/languages" replace />;
  return (
    <>
      <SeoHelmet {...seo} />
      <LanguagePage slug={slug} />
    </>
  );
}

// A working mic in a quiet room still peaks well above 0.002 (breath, room
// tone). Only a muted/dead input stays flat for 4 s.
const DEAD_MIC_AFTER_MS = 4000;
const DEAD_MIC_PEAK = 0.002;
// How long to keep the socket open after Stop for the server's final turn.
// The server's own grace period is 3 s; this only has to outlast it.
const STOP_FLUSH_WAIT_MS = 4500;

function App() {
  const [isListening, setIsListening] = useState(false);
  const [language, setLanguage] = useState('');
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState('');
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [serverUrl, setServerUrl] = useState(null);
  const [showTimeoutModal, setShowTimeoutModal] = useState(false);
  const [showAudioTip, setShowAudioTip] = useState(() => {
    try { return localStorage.getItem('langshazam.audioTipDismissed') !== '1'; }
    catch { return true; }
  });
  const [signedIn, setSignedIn] = useState(isSignedIn);
  // Detection is a signed-in feature. Tapping the mic while signed out opens
  // this sign-up modal instead of starting a session (the server refuses
  // anonymous sessions too, so the gate is not just cosmetic).
  const [showSignInGate, setShowSignInGate] = useState(false);
  // Mirrors the AuthPanel's mode so the modal headline matches the form.
  const [gateMode, setGateMode] = useState('register');

  const wsRef = useRef(null);
  const audioContextRef = useRef(null);
  const streamRef = useRef(null);
  const animFrameRef = useRef(null);
  const timeoutRef = useRef(null);
  // The detected-language card lives below the mic. On a phone it's often
  // just below the fold, so we scroll it into view when `language` first
  // becomes truthy. The ref lets us call scrollIntoView on the actual node.
  const resultCardRef = useRef(null);
  const detectTrackedRef = useRef(false);
  // Dead-mic detector: the worklet reports each chunk's peak amplitude. If
  // nothing above the floor arrives in the first few seconds, warn right away
  // instead of letting someone talk into a muted mic for a minute.
  const micLevelRef = useRef({ startedAt: 0, maxPeak: 0, warned: false });

  useEffect(() => {
    ServerDiscovery.discoverServer().then(setServerUrl);
  }, []);

  useEffect(() => subscribeAuth(() => setSignedIn(isSignedIn())), []);

  // Design-review aid: on localhost, /?demo=<Language> renders the
  // post-detection state without a microphone. Never active in production.
  useEffect(() => {
    if (!['localhost', '127.0.0.1'].includes(window.location.hostname)) return;
    const demo = new URLSearchParams(window.location.search).get('demo');
    if (!demo) return;
    setLanguage(demo);
    setTranscript('Merhaba, bugün hava çok güzel. Yarın İstanbul\'a gidiyorum, sonra da Ankara\'ya geçeceğim.');
  }, []);

  // Multi-modal feedback the moment a language lands so the user notices
  // even if their thumb hasn't scrolled yet:
  //   1. Haptic pulse on devices that support it (most mobile browsers).
  //      Pattern: short-pause-short, distinctive vs an incoming-call buzz.
  //   2. Smooth auto-scroll so the full result card sits in the viewport.
  //   3. (The hint text under the mic also flips to show the language —
  //       see hintText() below.)
  useEffect(() => {
    if (!language) return;
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      try { navigator.vibrate([60, 30, 60]); } catch { /* ignore */ }
    }
    if (resultCardRef.current) {
      resultCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [language]);

  const showToast = useCallback((message, type = 'success', durationMs) => {
    setToast({ show: true, message, type, durationMs });
  }, []);

  const hideToast = useCallback(() => {
    setToast(prev => ({ ...prev, show: false }));
  }, []);

  const cleanup = useCallback(() => {
    if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
    if (animFrameRef.current) { cancelAnimationFrame(animFrameRef.current); animFrameRef.current = null; }
    if (audioContextRef.current) { audioContextRef.current.close().catch(() => {}); audioContextRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
  }, []);

  // Tapping Stop releases the mic immediately but keeps the socket open for
  // a moment: AssemblyAI finalizes the last phrase ~0.3 s *after* Stop and
  // the server relays it over this same socket. Closing eagerly threw that
  // result away — production lost 55 detections in five days to it. The
  // server closes the socket when it is done; the timer is a safety net.
  const finishingRef = useRef(null);
  const [finishing, setFinishing] = useState(false);

  // Bumps each time a session (mic or upload) ends with a language, which
  // reopens the Polyvox pop-up. Mid-session language updates don't count.
  const [detectDoneCount, setDetectDoneCount] = useState(0);
  const resultReady = !!language && !isListening && !finishing;
  useEffect(() => {
    if (resultReady) setDetectDoneCount((n) => n + 1);
  }, [resultReady]);

  const stopListening = useCallback(() => {
    const ws = wsRef.current;
    cleanup();
    setIsListening(false);
    if (!ws) return;

    if (ws.readyState !== WebSocket.OPEN || finishingRef.current === ws) {
      wsRef.current = null;
      finishingRef.current = null;
      setFinishing(false);
      try { ws.onclose = null; ws.onerror = null; ws.onmessage = null; ws.close(); } catch (_) { /* ignore */ }
      return;
    }

    try { ws.send(JSON.stringify({ type: 'stop' })); } catch (_) { /* ignore */ }
    finishingRef.current = ws;
    setFinishing(true);

    const finish = () => {
      clearTimeout(timer);
      if (wsRef.current === ws) wsRef.current = null;
      if (finishingRef.current === ws) finishingRef.current = null;
      setFinishing(false);
      try { ws.onclose = null; ws.onerror = null; ws.onmessage = null; ws.close(); } catch (_) { /* ignore */ }
    };
    const timer = setTimeout(finish, STOP_FLUSH_WAIT_MS);
    ws.onclose = finish;
    ws.onerror = finish;
  }, [cleanup]);

  useEffect(() => {
    return () => {
      if (wsRef.current) { try { wsRef.current.close(); } catch (_) {} wsRef.current = null; }
      if (audioContextRef.current) { audioContextRef.current.close().catch(() => {}); audioContextRef.current = null; }
      if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleMicClick = async () => {
    if (isListening) {
      stopListening();
      return;
    }

    if (!signedIn) {
      setShowSignInGate(true);
      trackEvent('sign_in_gate_shown');
      return;
    }

    try {
      if (!serverUrl) throw new Error('Server not ready. Please try again.');

      setLanguage('');
      setTranscript('');
      setError('');

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      try {
        const audioContext = new AudioContext({ sampleRate: 16000 });
        audioContextRef.current = audioContext;
        const source = audioContext.createMediaStreamSource(stream);

        await audioContext.audioWorklet.addModule('/pcm-processor.js');
        const workletNode = new AudioWorkletNode(audioContext, 'pcm-processor');
        source.connect(workletNode);

        const muteNode = audioContext.createGain();
        muteNode.gain.value = 0;
        workletNode.connect(muteNode);
        muteNode.connect(audioContext.destination);

        const wsUrl = (() => {
          const token = getToken();
          if (!token) return serverUrl;
          const sep = serverUrl.includes('?') ? '&' : '?';
          return `${serverUrl}${sep}token=${encodeURIComponent(token)}`;
        })();
        const ws = new WebSocket(wsUrl);
        ws.binaryType = 'arraybuffer';
        wsRef.current = ws;

        ws.onopen = () => {
          setIsListening(true);
          detectTrackedRef.current = false;
          micLevelRef.current = { startedAt: Date.now(), maxPeak: 0, warned: false };
          trackEvent('detect_start');
        };

        ws.onclose = () => {
          if (wsRef.current === ws) { wsRef.current = null; cleanup(); setIsListening(false); }
        };

        ws.onerror = () => {
          if (wsRef.current === ws) {
            wsRef.current = null;
            setError('Connection error');
            showToast('Connection error', 'error');
            cleanup();
            setIsListening(false);
          }
        };

        ws.onmessage = (event) => {
          const msg = JSON.parse(event.data);
          if (msg.type === 'language') {
            setLanguage(msg.language);
            if (!detectTrackedRef.current) {
              detectTrackedRef.current = true;
              trackEvent('detect_result', { outcome: 'language' });
            }
          } else if (msg.type === 'transcript') {
            // Pushed live on every finalized phrase; the session keeps going.
            setTranscript(msg.text);
          } else if (msg.type === 'timeout') {
            stopListening();
            setShowTimeoutModal(true);
            if (!detectTrackedRef.current) {
              detectTrackedRef.current = true;
              trackEvent('detect_result', { outcome: 'timeout' });
            }
          } else if (msg.type === 'no_detection') {
            stopListening();
            const fallback = "Try again";
            const toastType = ['unrecognized', 'low_quality', 'mixed_languages'].includes(msg.category)
              ? 'warning'
              : 'error';
            showToast(msg.message || fallback, toastType, 4000);
            if (!detectTrackedRef.current) {
              detectTrackedRef.current = true;
              trackEvent('detect_result', { outcome: 'none' });
            }
          } else if (msg.type === 'error') {
            if (msg.code === 'sign_in_required') {
              stopListening();
              setShowSignInGate(true);
              return;
            }
            setError(msg.message);
            showToast(msg.message, 'error');
          }
        };

        workletNode.port.onmessage = (event) => {
          const { pcm, peak } = event.data;
          if (ws.readyState === WebSocket.OPEN) ws.send(pcm);

          const level = micLevelRef.current;
          if (peak > level.maxPeak) level.maxPeak = peak;
          if (!level.warned && level.startedAt && Date.now() - level.startedAt > DEAD_MIC_AFTER_MS
              && level.maxPeak < DEAD_MIC_PEAK) {
            level.warned = true;
            showToast(
              "We can't hear anything from your microphone. Check it isn't muted or in use by another app.",
              'warning',
              6000,
            );
            trackEvent('detect_dead_mic');
          }
        };

      } catch (innerErr) {
        cleanup();
        throw innerErr;
      }

    } catch (err) {
      console.error('Error:', err);
      setError(err.message);
      showToast(err.message, 'error');
      setIsListening(false);
    }
  };

  const hintText = () => {
    if (isListening && language) return `Detected: ${language} — keep speaking, the transcript updates live`;
    if (!isListening && finishing && !language) return 'Finishing up…';
    if (!isListening && language) return `Detected: ${language}`;
    if (isListening) return 'Listening… speak clearly for several seconds';
    return 'Tap to detect language';
  };

  return (
    <Router>
      <Analytics />
      <TermsGate />
      <Routes>
        <Route path="/" element={
          <div className="app">
            <SeoHelmet {...SEO.home} />
            <header className="home-topbar">
              <div className="lng-topbar-actions">
                <AccountButton />
              </div>
            </header>
            <div className="brand">
              <Logo />
              <h1 className="title">LangShazam</h1>
            </div>
            <p className="subtitle">The Shazam for languages — state-of-the-art spoken language detection, with a live transcript</p>

            <div className="home-hero-stack">
              {!signedIn && (
                <p className="sign-in-hint">
                  <Link to="/account">Sign in</Link> to detect languages — your detections, live transcripts, and recordings are saved to your account.
                </p>
              )}

              {!language && (
              <Link to="/langle" className="daily-cta">
                <span className="daily-cta-text">Play <strong>Langle</strong> — listen and guess the language</span>
                <span className="daily-cta-arrow">→</span>
              </Link>
              )}

              {showAudioTip && (
                <div className="audio-tip" role="status">
                  <p className="audio-tip-text">
                    For best results, speak clearly for several seconds in one language.
                    Short, quiet, or noisy audio often can’t be identified.
                  </p>
                  <button
                    type="button"
                    className="audio-tip-dismiss"
                    aria-label="Dismiss tip"
                    onClick={() => {
                      setShowAudioTip(false);
                      try { localStorage.setItem('langshazam.audioTipDismissed', '1'); } catch {}
                    }}
                  >
                    ×
                  </button>
                </div>
              )}
            </div>

            <div className={`mic-area${isListening ? ' listening' : ''}`}>
              <div className="mic-glow" />
              <span className="ring"></span>
              <span className="ring"></span>
              <span className="ring"></span>
              <button
                className={`mic-btn${isListening ? ' listening' : ''}`}
                onClick={handleMicClick}
              >
                {isListening ? <StopIcon /> : <MicIcon />}
              </button>
            </div>

            {isListening && <Waveform />}

            <p className="mic-hint">{hintText()}</p>

            {!isListening && !finishing && (
              <UploadDetect
                signedIn={signedIn}
                onRequireSignIn={() => { setShowSignInGate(true); trackEvent('sign_in_gate_shown', { from: 'upload' }); }}
                onResult={(data) => {
                  setError('');
                  setLanguage(data.language || '');
                  setTranscript(data.transcript || '');
                  if (!data.language) {
                    showToast(data.message || "We couldn't hear speech in that file.", 'warning', 5000);
                  } else if (data.truncated) {
                    showToast(`Only the first 10 minutes were analyzed (file is ${Math.round(data.audioDuration / 60)} min).`, 'warning', 6000);
                  }
                }}
                onError={(msg) => { setError(msg); showToast(msg, 'error', 5000); }}
              />
            )}

            {isListening && language && (
              <div className="actions">
                <button className="btn btn-danger" onClick={stopListening}>
                  Stop
                </button>
              </div>
            )}

            {isListening && !language && (
              <div className="actions">
                <button className="btn btn-danger" onClick={stopListening}>
                  Cancel
                </button>
              </div>
            )}

            {language && (
              <div className="result-card" ref={resultCardRef}>
                <div className="result-header">
                  <GlobeIcon />
                  <div className="result-label">Detected Language</div>
                </div>
                <div className="result-language">{language}</div>
              </div>
            )}

            {language && !isListening && (
              <LangleAfterDetect language={language} />
            )}

            {language && signedIn && (
              <p className="saved-hint">
                Saved to your <Link to="/account">account</Link> — replay anytime.
              </p>
            )}

            {transcript && (
              <div className={`result-card transcript-card${isListening ? ' live' : ''}`}>
                <div className="result-header">
                  <DocIcon />
                  <div className="result-label">Transcript</div>
                </div>
                <div className="result-transcript">{transcript}</div>
              </div>
            )}

            {language && !isListening && (
              <PolyvoxPromo placement="detect" language={language} />
            )}

            <PolyvoxPopup placement="detect" language={language} trigger={detectDoneCount} />

            {error && <p className="error-msg">{error}</p>}

            {showSignInGate && (
              <div className="modal-overlay" onClick={() => setShowSignInGate(false)}>
                <div className="modal-card gate-card" onClick={e => e.stopPropagation()}>
                  <h2 className="modal-title">
                    {gateMode === 'login' && 'Sign in to detect languages'}
                    {gateMode === 'register' && 'Create a free account to detect languages'}
                    {gateMode === 'forgot' && 'Reset your password'}
                  </h2>
                  <p className="modal-subtitle">
                    {gateMode === 'login'
                      ? 'Welcome back. Your detections, live transcripts, and recordings are waiting on your account.'
                      : gateMode === 'register'
                        ? 'Sign up in seconds with Google or email. Every detection, live transcript, and recording is saved to your account so you can replay it later.'
                        : "Enter your email and we'll send you a link to set a new password."}
                  </p>
                  <AuthPanel
                    nextPath="/"
                    initialMode="register"
                    onModeChange={setGateMode}
                    onSuccess={() => {
                      setShowSignInGate(false);
                      showToast('Signed in — tap the mic to detect', 'success');
                    }}
                  />
                  <button className="modal-btn" onClick={() => setShowSignInGate(false)}>
                    Not now
                  </button>
                </div>
              </div>
            )}

            {showTimeoutModal && (
              <div className="modal-overlay" onClick={() => setShowTimeoutModal(false)}>
                <div className="modal-card" onClick={e => e.stopPropagation()}>
                  <div className="modal-icon-wrap">
                    <svg className="modal-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                  </div>
                  <h2 className="modal-title">Session Limit Reached</h2>
                  <p className="modal-subtitle">
                    Free sessions are limited to 3 minutes. Need longer transcriptions
                    or want to integrate language detection into your product?
                  </p>
                  <div className="modal-divider" />
                  <p className="modal-cta">Reach out for unlimited access</p>
                  <div className="modal-links">
                    <a href="mailto:support@langshazam.com" className="modal-link">
                      <span className="modal-link-icon">✉</span>
                      support@langshazam.com
                    </a>
                    <a href="https://x.com/hi_kaank" target="_blank" rel="noopener noreferrer" className="modal-link">
                      <span className="modal-link-icon">𝕏</span>
                      @hi_kaank
                    </a>
                  </div>
                  <button className="modal-btn" onClick={() => setShowTimeoutModal(false)}>
                    Try Again
                  </button>
                </div>
              </div>
            )}

            <Toast show={toast.show} message={toast.message} type={toast.type} durationMs={toast.durationMs} onHide={hideToast} />

            {/* How it works */}
            <section className="seo-section how-it-works" id="how-it-works">
              <h2>How It Works</h2>
              <p className="section-subtitle">Identify any spoken language in three simple steps</p>
              <div className="steps-grid">
                <div className="step-card">
                  <div className="step-number">1</div>
                  <h3>Tap the Microphone</h3>
                  <p>Press the mic button to start real-time audio capture from your device.</p>
                </div>
                <div className="step-card">
                  <div className="step-number">2</div>
                  <h3>Speak or Play Audio</h3>
                  <p>Say something — or hold your phone near a conversation, podcast, or video.</p>
                </div>
                <div className="step-card">
                  <div className="step-number">3</div>
                  <h3>Get Instant Results</h3>
                  <p>LangShazam names the language in seconds and keeps transcribing live while you speak.</p>
                </div>
              </div>
            </section>

            {/* Supported languages preview */}
            <section className="seo-section supported-languages" id="supported-languages">
              <h2>32 Supported Languages</h2>
              <p className="section-subtitle">
                From Arabic to Zulu — detect and transcribe spoken languages from every continent
              </p>
              <div className="lang-chips">
                {LANGUAGES.slice(0, 24).map(l => (
                  <Link key={l.slug} to={`/languages/${l.slug}`} className="lang-chip">{l.name}</Link>
                ))}
                <Link to="/languages" className="lang-chip lang-chip-more">
                  +{LANGUAGES.length - 24} more →
                </Link>
              </div>
              <Link to="/languages" className="section-link">View all supported languages →</Link>
            </section>

            {/* Use cases — explicitly targets Search Console queries that
                currently get impressions but zero clicks: "detect spoken
                language", "language detector audio", "detect language audio",
                "audio language detector". The literal phrasing in headings
                helps both ranking and snippet generation. */}
            <section className="seo-section use-cases" id="use-cases">
              <h2>Detect Spoken Language from Audio in Real Time</h2>
              <p className="section-subtitle">
                LangShazam is a free <strong>audio language detector</strong> — think of it as Shazam for
                languages. We are building state-of-the-art language detection: whatever you can hear,
                we can identify and transcribe.
              </p>
              <div className="use-case-grid">
                <div className="use-case-card">
                  <h3>Identify a Language from a Recording</h3>
                  <p>
                    Got a voicemail, voice note, or audio clip in a language you don't recognise?
                    Play it near the mic and LangShazam tells you what it is in seconds.
                  </p>
                </div>
                <div className="use-case-card">
                  <h3>Detect Language from a Live Conversation</h3>
                  <p>
                    Overheard speech in a café, taxi, or on holiday? Tap the mic to detect the spoken
                    language without interrupting anyone or pulling out a translator app.
                  </p>
                </div>
                <div className="use-case-card">
                  <h3>Find Out What Language a Podcast or Video Is In</h3>
                  <p>
                    Hold your phone up to a podcast, YouTube video, or radio stream. Our audio
                    language detection identifies the language and transcribes it live.
                  </p>
                </div>
                <div className="use-case-card">
                  <h3>Tell Apart Similar-Sounding Languages</h3>
                  <p>
                    Spanish vs. Portuguese? Hindi vs. Urdu? Norwegian vs. Danish? LangShazam listens to
                    the audio itself and separates closely related languages that text-based tools confuse.
                  </p>
                </div>
              </div>
              <p className="use-case-cta">
                Not sure what you're listening to? See{' '}
                <Link to="/what-language-is-this">what language is this audio</Link> and{' '}
                <Link to="/what-language-am-i-hearing">what language am I hearing</Link>.
              </p>
              <p className="use-case-cta">
                Want a daily game? Play <Link to="/langle">Langle</Link> — listen and guess
                the language.
              </p>
            </section>

            {/* FAQ */}
            <section className="seo-section faq-section" id="faq">
              <h2>Frequently Asked Questions</h2>
              <div className="faq-list">
                <details className="faq-item">
                  <summary>How does LangShazam detect language from audio?</summary>
                  <p>
                    LangShazam streams your audio to AssemblyAI's Universal-3.6 Pro real-time speech model,
                    which transcribes and identifies the spoken language natively across 32 languages, with
                    a confidence score on every phrase.
                  </p>
                </details>
                <details className="faq-item">
                  <summary>Is it free to use?</summary>
                  <p>
                    Yes — LangShazam is free. Create an account with Google or email, tap the mic, and start
                    detecting. Your detections, transcripts and recordings are saved for you.
                  </p>
                </details>
                <details className="faq-item">
                  <summary>What languages can be detected?</summary>
                  <p>
                    LangShazam supports 32 languages including English, Spanish, French, Arabic, Chinese,
                    Hindi, Portuguese, Russian, Japanese, Korean, Turkish, and more.{' '}
                    <Link to="/languages">See the full list.</Link>
                  </p>
                </details>
                <details className="faq-item">
                  <summary>How accurate is the detection?</summary>
                  <p>
                    We are building state-of-the-art language detection. LangShazam listens to the audio
                    itself with a real-time multilingual speech model, so after a few seconds of clear speech
                    accuracy is very high, including for closely related languages.
                  </p>
                </details>
                <details className="faq-item">
                  <summary>Can I get a transcript of what was said?</summary>
                  <p>
                    Yes — every session shows a live transcript as you speak, in the detected language's own
                    script, and it is saved to your account.
                  </p>
                </details>
                <details className="faq-item">
                  <summary>Can I upload a recording instead of using the mic?</summary>
                  <p>
                    Yes — tap "Or upload a recording" under the mic. Voice notes, voicemails, podcast
                    episodes and videos all work; the first 10 minutes are analyzed, and uploads recognise
                    99+ languages. The result and transcript are saved to your account like a mic session.
                  </p>
                </details>
                <details className="faq-item">
                  <summary>Does it work on mobile?</summary>
                  <p>
                    Absolutely. LangShazam works in any modern browser — Safari, Chrome, Firefox — on both
                    desktop and mobile devices. No app download needed.
                  </p>
                </details>
                <details className="faq-item">
                  <summary>Can I save my detections and replay recordings?</summary>
                  <p>
                    Yes — detection needs a free account, and each mic session is saved to it:
                    detected language, live transcript, and a replayable recording you can revisit on your{' '}
                    <Link to="/account">account page</Link>.
                  </p>
                </details>
                <details className="faq-item">
                  <summary>Is my audio data stored?</summary>
                  <p>
                    Sessions are linked to your account so you can replay them on your account page, and you can
                    delete them there.
                    We don't sell, share, or use your audio for advertising or profiling. See our{' '}
                    <Link to="/privacy">Privacy Policy</Link> for details.
                  </p>
                </details>
              </div>
            </section>

            <footer className="footer">
              <div className="footer-inner">
                <Link to="/langle">Langle</Link>
                <Link to="/account">Account</Link>
                <Link to="/privacy">Privacy</Link>
                <Link to="/terms">Terms</Link>
                <Link to="/about">About</Link>
                <Link to="/contact">Contact</Link>
                <Link to="/languages">Languages</Link>
                <a
                  href={polyvoxUrl('footer')}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackPolyvoxClick('footer')}
                >
                  Polyvox
                </a>
              </div>
            </footer>
          </div>
        } />
        <Route path="/privacy" element={<><SeoHelmet {...SEO.privacy} /><Privacy /></>} />
        <Route path="/terms" element={<><SeoHelmet {...SEO.terms} /><Terms /></>} />
        <Route path="/about" element={<><SeoHelmet {...SEO.about} /><About /></>} />
        <Route path="/contact" element={<><SeoHelmet {...SEO.contact} /><Contact /></>} />
        <Route path="/languages" element={<><SeoHelmet {...SEO.languages} /><Languages /></>} />
        <Route path="/languages/:slug" element={<LanguageRoute />} />
        {QUESTIONS.map((q) => (
          <Route key={q.slug} path={`/${q.slug}`} element={<><SeoHelmet {...SEO[`q-${q.slug}`]} /><QuestionPage slug={q.slug} /></>} />
        ))}
        <Route path="/daily" element={<Navigate to="/langle" replace />} />
        <Route path="/langle" element={<><SeoHelmet {...SEO.langle} /><DailyLanding /></>} />
        <Route path="/langle/classic" element={<><SeoHelmet {...SEO.langle} /><Daily /></>} />
        <Route path="/langle/easy" element={<><SeoHelmet {...SEO.langle} /><Daily difficulty="easy" /></>} />
        <Route path="/langle/hard" element={<><SeoHelmet {...SEO.langle} /><Daily difficulty="hard" /></>} />
        <Route path="/account" element={<Account />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/auth/verify" element={<AuthVerify />} />
        <Route path="/auth/reset" element={<AuthReset />} />
        <Route path="/auth/unsubscribe-daily" element={<AuthUnsubscribeDaily />} />
        <Route path="/group/:id" element={<Group />} />
      </Routes>
    </Router>
  );
}

export default App;
